import React, { useContext, useCallback, useState } from "react";
import styled, { ThemeContext } from "styled-components";
import WBCCollapse from "../elements/WBCCollapse";
import PlayerTable from "./provisional/PlayerTable";
import RosterContext from "../../contexts/RosterContext";
import WBCYearsContext from "../../contexts/WBCYearsContext";
import SelectedTeamContext from "../../contexts/SelectedTeamContext";
import TournamentTypeContext from "../../contexts/TournamentTypeContext";
import { FinalizeWrapper, FinalizeButton } from "../roster/RosterStyles";
import GlobalModalContext from "../../contexts/GlobalModalContext";
import { PROVISIONAL_LIST } from "../../constants/RosterListConstants";
import AuthContext from "../../contexts/AuthContext";
import { formatRosterDate } from "../../utils/DateUtils";
import CustomDownloadModal from "../shared/CustomDownloadModal";
import { PROVISIONAL_ID } from "../../constants/TournamentStatusConstants";
import { MAIN_ID, QUALIFYING, QUALIFYING_ID } from "../../constants/TournamentTypeConstants";
import { SubtitleText, StatusText } from "../elements/CommonStyledComponents";

const Container = styled.div`
  margin-bottom: 21px;
`;

const ProvisionalCollapse = styled(WBCCollapse)`
  .wbc-collapse-subtitle {
    color: ${({ invalid, theme }) => (invalid ? theme.wbcRed : undefined)};
  }
`;

const ProvisionalRoster = () => {
  // context(s) && state
  const theme = useContext(ThemeContext);
  const rosterContext = useContext(RosterContext);
  const { selectedWbcYear } = useContext(WBCYearsContext);
  const selectedTeamContext = useContext(SelectedTeamContext);
  const globalModalContext = useContext(GlobalModalContext);
  const tournamentType = useContext(TournamentTypeContext);
  const { isWBCIadmin, isFedAdmin, isTeamCoordinator } = useContext(AuthContext).state;
  const [openDownloadModal, setOpenDownloadModal] = useState(false);

  // variable(s)
  const { team } = selectedTeamContext.state;
  const { teamId: fedTeamId } = team;
  const { players } = rosterContext.state.provisional;
  const { length: size } = rosterContext.state.provisional.players;
  const { provisionalIsOpen } = rosterContext.state;
  const { dispatch: rosterDispatch } = rosterContext;
  const settings = (selectedWbcYear && selectedWbcYear[`${tournamentType}TournamentSettings`]) || null;
  const provisionalMax = settings && settings.rosterNumbers.provisionalMax;
  const finalizedDate = team.finalizedStatus?.finalizedProvisionalDate;

  const rosterType = PROVISIONAL_LIST;

  const finalizeRoster = () => {
    globalModalContext.dispatch({
      type: "openFinalizeRosterModal",
      headerText: `Finalize ${rosterType}`,
      content: { team, prefix: tournamentType, rosterType }
    });
  };

  // function(s)
  const exportFn = useCallback(() => {
    setOpenDownloadModal(true);
  }, []);

  const clickedProvisionalCollapse = isOpen => {
    rosterDispatch({
      type: "setProvisionalIsOpen",
      isOpen
    });
  };

  return (
    settings && (
      <Container>
        <ProvisionalCollapse
          exportable
          startsOpen={provisionalIsOpen}
          openCloseCallback={clickedProvisionalCollapse}
          title={`Provisional (${size})`}
          subTitle={`${provisionalMax} player max`}
          invalid={size > provisionalMax}
          exportFn={exportFn}
        >
          <SubtitleText>
            Add up to 35 players to the provisional roster. Players cannot be added to a Final Roster until their
            Eligibility is{" "}
            <StatusText
              bgColor={theme.eligibilityStatus.approved.background}
              color={theme.eligibilityStatus.approved.color}
            >
              Approved
            </StatusText>
          </SubtitleText>
          <PlayerTable />
          {(isWBCIadmin || isTeamCoordinator || (isFedAdmin && !finalizedDate)) && (
            <FinalizeWrapper>
              <FinalizeButton
                onClick={event => {
                  event.stopPropagation();
                  finalizeRoster();
                }}
              >
                Finalize {rosterType}
              </FinalizeButton>
            </FinalizeWrapper>
          )}
          {!!finalizedDate && <FinalizeWrapper>Finalized on {formatRosterDate(finalizedDate)}</FinalizeWrapper>}
        </ProvisionalCollapse>
        <CustomDownloadModal
          profileIds={players?.map(player => player?.profileId)}
          tournamentType={tournamentType === QUALIFYING ? QUALIFYING_ID : MAIN_ID}
          fedTeam={fedTeamId}
          federation={team?.label}
          rosterType={PROVISIONAL_ID}
          rosterName={PROVISIONAL_LIST}
          isOpen={openDownloadModal}
          onClose={() => setOpenDownloadModal(false)}
        />
      </Container>
    )
  );
};

export default ProvisionalRoster;
